import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from "@exhousing/design-system";
import { CalendarRange, FileText, Pencil, ShieldCheck } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { useUpdateContract } from "@/hooks/api/useContracts";
import { LEASE_MODELS, leaseModelLabel } from "@/lib/leaseModels";
import { money } from "@/lib/money";
import type { components } from "@/types/api";

type Contract = components["schemas"]["ContractResponse"];

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function ContractTermsCard({ contract }: { contract: Contract }) {
  const update = useUpdateContract(contract.id);
  const [editing, setEditing] = useState(false);
  const isDraft = contract.status === "draft";
  const [startDate, setStartDate] = useState(contract.start_date ?? "");
  const [signedDate, setSignedDate] = useState(contract.signed_date ?? "");
  const [endDate, setEndDate] = useState(contract.end_date ?? "");
  const [bankGuarantee, setBankGuarantee] = useState(contract.bank_guarantee);
  const [guaranteeAmount, setGuaranteeAmount] = useState(
    contract.bank_guarantee_amount != null ? String(contract.bank_guarantee_amount) : "",
  );
  const [rentFromCompany, setRentFromCompany] = useState(contract.rent_from_company);
  const [leaseVariant, setLeaseVariant] = useState(contract.lease_variant ?? "roz");

  const save = (e: React.FormEvent) => {
    e.preventDefault();
    update.mutate(
      {
        // Dates + lease model are signed terms — only editable while drafting.
        ...(isDraft
          ? {
              start_date: startDate || null,
              signed_date: signedDate || null,
              end_date: endDate || null,
              lease_variant: leaseVariant,
            }
          : {}),
        bank_guarantee: bankGuarantee,
        bank_guarantee_amount:
          guaranteeAmount.trim() === "" ? null : Number(guaranteeAmount),
        rent_from_company: rentFromCompany,
      },
      {
        onSuccess: () => {
          toast.success("Terms updated");
          setEditing(false);
        },
        onError: () => toast.error("Could not update terms"),
      },
    );
  };

  // --- Plain-English meaning of each field ---
  const termMeaning =
    contract.term === "indefinite"
      ? "Open-ended — runs until either party gives notice."
      : contract.end_date
        ? `Fixed term — ends ${fmtDate(contract.end_date)}.`
        : "Fixed term — no end date set yet.";

  const hasDeposit = contract.deposit_amount != null && contract.deposit_amount > 0;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Term &amp; guarantees</CardTitle>
        {!editing && (
          <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
            <Pencil className="size-4" />
            Edit
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Term — how long the tenancy runs */}
        <div className="flex items-start gap-3">
          <CalendarRange className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium capitalize">{contract.term} term</p>
            <p className="text-sm text-muted-foreground">{termMeaning}</p>
            {editing && isDraft ? (
              <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="space-y-1">
                  <Label htmlFor="start_date">Start date</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="h-9"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="signed_date">Signed date</Label>
                  <Input
                    id="signed_date"
                    type="date"
                    value={signedDate}
                    onChange={(e) => setSignedDate(e.target.value)}
                    className="h-9"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="end_date">End date</Label>
                  <Input
                    id="end_date"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="h-9"
                  />
                </div>
              </div>
            ) : (
              <>
                <p className="mt-0.5 text-xs text-muted-foreground tabular-nums">
                  Starts {fmtDate(contract.start_date)} · signed{" "}
                  {fmtDate(contract.signed_date)}
                </p>
                {editing && !isDraft && (
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    Dates are locked once active — the end date changes by ending the
                    contract.
                  </p>
                )}
              </>
            )}
          </div>
        </div>

        {/* Lease model — the legal template the Documents section generates */}
        <div className="flex items-start gap-3 border-t border-border/60 pt-4">
          <FileText className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium">Lease model</p>
            {editing && isDraft ? (
              <>
                <select
                  value={leaseVariant}
                  onChange={(e) => setLeaseVariant(e.target.value)}
                  className="mt-1 h-9 rounded-md border border-input bg-background px-2.5 text-sm outline-none focus:border-primary"
                >
                  {LEASE_MODELS.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-muted-foreground">
                  Determines which lease template the Documents section generates.
                </p>
              </>
            ) : (
              <>
                <p className="mt-0.5 text-sm text-muted-foreground">
                  <span className="text-foreground">
                    {leaseModelLabel(contract.lease_variant)}
                  </span>{" "}
                  — the Documents section generates this lease template.
                </p>
                {editing && !isDraft && (
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    Locked once active — the lease model is a signed term.
                  </p>
                )}
              </>
            )}
          </div>
        </div>

        {/* Security — how the tenancy is guaranteed */}
        <div className="flex items-start gap-3 border-t border-border/60 pt-4">
          <ShieldCheck className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
          <div className="min-w-0">
            <p className="text-sm font-medium">Security</p>
            {editing ? (
              <p className="text-xs text-muted-foreground">
                The deposit is managed on the Deposit tab. Set a bank guarantee below if
                the tenant posts one instead of cash.
              </p>
            ) : (
              <ul className="mt-0.5 space-y-1 text-sm text-muted-foreground">
                {hasDeposit && (
                  <li>
                    <span className="text-foreground">
                      Cash deposit · {money(contract.deposit_amount)}
                    </span>{" "}
                    — held and refunded (minus any deductions) at move-out.
                  </li>
                )}
                {contract.bank_guarantee && (
                  <li>
                    <span className="text-foreground">
                      Bank guarantee
                      {contract.bank_guarantee_amount != null
                        ? ` · ${money(contract.bank_guarantee_amount)}`
                        : ""}
                    </span>{" "}
                    — the tenant's bank stands surety instead of a cash deposit.
                  </li>
                )}
                {!hasDeposit && !contract.bank_guarantee && (
                  <li>No deposit or bank guarantee recorded.</li>
                )}
              </ul>
            )}

            {editing && (
              <div className="mt-3 space-y-3">
                <label className="flex cursor-pointer items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={bankGuarantee}
                    onChange={(e) => setBankGuarantee(e.target.checked)}
                    className="size-4 rounded border-input accent-primary"
                  />
                  Tenant posts a bank guarantee
                </label>
                {bankGuarantee && (
                  <div className="space-y-1.5 sm:max-w-xs">
                    <Label htmlFor="bank_guarantee_amount">Guarantee amount (€)</Label>
                    <Input
                      id="bank_guarantee_amount"
                      type="number"
                      step="0.01"
                      value={guaranteeAmount}
                      onChange={(e) => setGuaranteeAmount(e.target.value)}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Who pays — VAT treatment */}
        <div className="border-t border-border/60 pt-4">
          {editing ? (
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={rentFromCompany}
                onChange={(e) => setRentFromCompany(e.target.checked)}
                className="size-4 rounded border-input accent-primary"
              />
              Rent is paid by a company
            </label>
          ) : (
            <p className="text-sm">
              <span className="text-muted-foreground">Paid by: </span>
              {contract.rent_from_company
                ? "a company — rent is VAT-taxable."
                : "the tenant (private) — residential rent, VAT-exempt."}
            </p>
          )}
        </div>

        {editing && (
          <form onSubmit={save} className="flex gap-2 border-t border-border/60 pt-4">
            <Button type="submit" size="sm" disabled={update.isPending}>
              {update.isPending ? "Saving…" : "Save"}
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </form>
        )}
      </CardContent>
    </Card>
  );
}
